import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteSectionModel, CampaignQuoteSectionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignQuoteSections']

type CampaignQuoteSectionFilters = {
  campaign_quote_id?: number,
  string?: string,
}

type UseCampaignQuoteSectionsOptions = {
  filters?: CampaignQuoteSectionFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteSectionRequestOptions,
}

function useCampaignQuoteSections(options: UseCampaignQuoteSectionsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_quote_id: filterCampaignQuoteId,
    string: filterString,
  } = filters

  const {
    updatedEntities: {
      campaignQuoteSections: campaignQuoteSectionsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignQuoteSections } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignQuoteSections = useMemo(() => {
    const filtered = Object.values(campaignQuoteSections).filter((campaignQuoteSection: CampaignQuoteSectionModel) => {
      const { campaign_quote_id, id, title } = campaignQuoteSection

      const matchId = id !== undefined && id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchCampaignQuoteId = matchFilterNumber(campaign_quote_id, filterCampaignQuoteId)

      return matchCampaignQuoteId && matchString
    })

    return sortArrayBy(filtered, 'asc', 'sort')
  }, [campaignQuoteSectionsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignQuoteSectionIds = filteredCampaignQuoteSections.map(s => s.id)
  const filteredCampaignQuoteSectionsCount = filteredCampaignQuoteSections.length
  const hasFilteredCampaignQuoteSections = !!filteredCampaignQuoteSectionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignQuoteSectionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignQuoteSections } = useReduxAction(
    'campaignQuoteSections',
    'loadCampaignQuoteSections',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  const lastSectionIndex = filteredCampaignQuoteSections.length - 1

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuoteSectionIds,
    filteredCampaignQuoteSections,
    filteredCampaignQuoteSectionsCount,
    hasFilteredCampaignQuoteSections,
    lastSectionIndex,
    loading: loadingCampaignQuoteSections,
  }
}

export default useCampaignQuoteSections
